import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowUpRight, FileText, ExternalLink, Sparkles } from "lucide-react";

const LAW_REFS = [
  "NN 48/2026 · na snazi od 16.05.2026.",
  "NN 86/12, 143/13, 65/17, 14/19, 48/26",
  "NN 112/17, 34/18, 36/19, 98/19, 31/20, 74/22",
  "NN 153/13, 20/17, 39/19, 125/19",
  "NN 153/13, 65/17, 114/18, 39/19, 98/19",
  "NN 112/18, 39/22",
  "NN 63/19, 128/22",
];

export default function PropisiPage() {
  const { t } = useTranslation();
  const categories = t("propisi.categories", { returnObjects: true });
  const laws = t("propisi.laws", { returnObjects: true });
  const categoriesArr = Array.isArray(categories) ? categories : [];
  const lawsArr = Array.isArray(laws) ? laws : [];

  return (
    <div data-testid="propisi-page">
      <Seo title={t("propisi.seoTitle")} path="/zakonski-propisi" description={t("propisi.seoDesc")} />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> {t("propisi.kicker")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                {t("propisi.heroTitle1")}<br />{t("propisi.heroTitle2")}<br />
                <span className="text-ink/40">{t("propisi.heroTitle3")}</span>
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              {t("propisi.heroLead")}
            </div>
          </div>
        </div>

        {/* 2026 update banner */}
        <div className="border-t border-hair bg-[#FFF5F4]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-start gap-4">
              <span className="bg-[#D92D20] text-white font-brand uppercase text-[11px] tracking-[0.22em] px-3 py-1.5 whitespace-nowrap">
                {t("propisi.newBadge")}
              </span>
              <p className="text-ink text-sm sm:text-[15px] leading-relaxed">
                {t("propisi.bannerText")}
              </p>
            </div>
            <a
              href="#law-00"
              onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="law-trigger-00"]')?.click(); document.querySelector('[data-testid="law-trigger-00"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D92D20] border-b border-[#D92D20]/40 hover:border-[#D92D20] whitespace-nowrap"
            >
              {t("propisi.bannerLink")}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
        <div className="overline text-ink-soft">{t("propisi.categoriesKicker")}</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink mt-3 leading-tight">
          {t("propisi.categoriesTitle")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 mt-10 border border-ink/10">
          {categoriesArr.map((c, i) => (
            <div key={c.label} className="bg-white p-6">
              <div className="font-display text-4xl text-ink/15 font-bold">0{i + 1}</div>
              <div className="font-display font-semibold text-ink mt-3">{c.label}</div>
              <div className="text-ink-soft text-sm mt-1.5 leading-relaxed">{c.area}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LAWS ACCORDION */}
      <section className="border-t border-hair surface">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="overline text-ink-soft">{t("propisi.lawsKicker")}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                {t("propisi.lawsTitle")}
              </h2>
            </div>
            <div className="md:col-span-5 text-ink-soft leading-relaxed">
              {t("propisi.lawsIntro")}
            </div>
          </div>

          <div className="mt-12 bg-white border border-hair">
            <Accordion type="single" collapsible className="w-full" data-testid="laws-accordion">
              {lawsArr.map((l, idx) => {
                const n = `0${idx}`;
                const highlight = idx === 0;
                const ref = LAW_REFS[idx] || "";
                const bullets = Array.isArray(l.bullets) ? l.bullets : null;
                return (
                  <AccordionItem
                    key={n}
                    value={n}
                    className={`border-b border-hair last:border-b-0 ${highlight ? "bg-[#FFF5F4]" : ""}`}
                  >
                    <AccordionTrigger
                      className="px-6 py-6 hover:no-underline data-[state=open]:bg-secondary/40"
                      data-testid={`law-trigger-${n}`}
                    >
                      <div className="flex items-start gap-6 text-left w-full">
                        <span className={`font-display text-2xl font-bold leading-none w-10 shrink-0 ${highlight ? "text-[#D92D20]" : "text-ink/25"}`}>
                          {highlight ? <Sparkles className="w-6 h-6" strokeWidth={1.5} /> : n}
                        </span>
                        <div className="flex-1">
                          <div className={`overline ${highlight ? "text-[#D92D20]" : "text-ink-soft"}`}>{l.short}</div>
                          <div className="font-display text-lg sm:text-xl font-semibold text-ink mt-1.5 tracking-tight">
                            {l.title}
                          </div>
                        </div>
                        <span className="hidden md:inline text-xs text-ink-soft shrink-0 mt-2">
                          {ref}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="md:pl-16 max-w-3xl">
                        <p className="text-ink leading-relaxed">{l.desc}</p>
                        {bullets && (
                          <ul className="mt-4 space-y-2">
                            {bullets.map((b, i) => (
                              <li key={i} className="flex gap-3 text-ink text-[15px] leading-relaxed">
                                <span className="text-[#D92D20] font-bold mt-0.5">◆</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="md:hidden text-xs text-ink-soft mt-3">{ref}</div>
                        <a
                          href={highlight ? "https://narodne-novine.nn.hr/eli/sluzbeni/2026/48/583" : "https://narodne-novine.nn.hr"}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 mt-5 text-sm text-ink font-medium border-b border-ink/40 pb-0.5 hover:border-ink"
                        >
                          {t("propisi.officialGazette")} <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </a>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          <div className="mt-10 border border-ink/15 bg-white p-6 flex items-start gap-4">
            <FileText className="w-5 h-5 text-ink mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-ink-soft leading-relaxed">
              <span className="text-ink font-medium">{t("propisi.noteLabel")}</span> {t("propisi.note")}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink leading-tight">
              {t("propisi.ctaTitle")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/prijava"
              data-testid="propisi-cta"
              className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors"
            >
              {t("propisi.ctaBtn")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
